import { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Spinner } from '../ui/Spinner';
import { createCdnTopupPayment, getPaymentStatus } from '../../api/payment.api';
import { applyPromocode } from '../../api/promocode.api';
import { useSubscriptionStore } from '../../store/subscriptionStore';
import type { PromocodeResult } from '../../types/promocode';
import { formatPrice } from '../../utils/formatPrice';
import { getApiErrorMessage } from '../../utils/apiError';
import {
  CDN_TOPUP_MODAL_HINT,
  CDN_TOPUP_MODAL_TITLE,
  CDN_TOPUP_PACKAGES_LABEL,
  CDN_TOPUP_PACKAGES_UNAVAILABLE,
  CDN_TOPUP_PROMO_DAYS_ERROR,
  CDN_TOPUP_SUCCESS,
  cdnPackageLabel,
  cdnPackagePerGb,
} from '../../texts/cdn';
import styles from './BuyCdnTrafficModal.module.css';

interface BuyCdnTrafficModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Вызывается после успешной оплаты в stub-режиме (без редиректа на ЮKassa) */
  onPurchased?: () => void;
}

/** Опрос статуса платежа в stub-режиме: 30 попыток по 2 с, как в BuyExtraKeyCard */
const POLL_MAX_ATTEMPTS = 30;
const POLL_INTERVAL_MS = 2000;

export function BuyCdnTrafficModal({ isOpen, onClose, onPurchased }: BuyCdnTrafficModalProps) {
  const { cdnPackages, fetchCdnPackages } = useSubscriptionStore();
  const [packagesFailed, setPackagesFailed] = useState(false);
  const [selectedGb, setSelectedGb] = useState<number | null>(null);

  const [paying, setPaying] = useState(false);
  const [error, setError] = useState('');

  const [promoCode, setPromoCode] = useState('');
  const [promoLoading, setPromoLoading] = useState(false);
  const [promoError, setPromoError] = useState('');
  const [appliedPromo, setAppliedPromo] = useState<PromocodeResult | null>(null);

  // Интервал опроса держим в ref, чтобы снять его при размонтировании (уход со страницы)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  useEffect(
    () => () => {
      if (pollRef.current) clearInterval(pollRef.current);
    },
    []
  );

  // Прайс тянем при первом открытии — на дашборде он больше нигде не нужен
  useEffect(() => {
    if (!isOpen || cdnPackages) return;
    let cancelled = false;
    fetchCdnPackages().then(() => {
      // Стор глотает ошибку и оставляет null — это и есть признак неудачи
      if (!cancelled && !useSubscriptionStore.getState().cdnPackages) {
        setPackagesFailed(true);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [isOpen, cdnPackages, fetchCdnPackages]);

  const packages = cdnPackages?.packages ?? [];
  const packagesLoading = isOpen && !cdnPackages && !packagesFailed;
  // Без явного выбора берём первый пакет — кнопка оплаты всегда показывает цену
  const selected = packages.find((p) => p.gb === selectedGb) ?? packages[0] ?? null;

  const hasPercent = !!appliedPromo && appliedPromo.discountPercent != null;
  const basePriceKopecks = selected?.priceKopecks ?? 0;
  const finalPriceKopecks =
    hasPercent && appliedPromo?.discountPercent != null
      ? Math.round((basePriceKopecks * (100 - appliedPromo.discountPercent)) / 100)
      : basePriceKopecks;

  const handleClose = () => {
    if (paying) return;
    setError('');
    setPromoError('');
    onClose();
  };

  const handleApplyPromo = async () => {
    const code = promoCode.trim();
    if (!code) return;
    setPromoLoading(true);
    setPromoError('');
    try {
      const result = await applyPromocode(code);
      // Промокоды с бонусными днями к докупке трафика не применимы (бекенд ответит 422)
      if (result.discountDays != null && result.discountPercent == null) {
        setAppliedPromo(null);
        setPromoError(CDN_TOPUP_PROMO_DAYS_ERROR);
        return;
      }
      setAppliedPromo(result);
    } catch (err) {
      setAppliedPromo(null);
      setPromoError(getApiErrorMessage(err, 'Не удалось применить промокод'));
    } finally {
      setPromoLoading(false);
    }
  };

  const handleRemovePromo = () => {
    setAppliedPromo(null);
    setPromoError('');
    setPromoCode('');
  };

  const stopPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  const handlePay = async () => {
    if (!selected) return;
    setPaying(true);
    setError('');
    try {
      const returnUrl = `${window.location.origin}/dashboard/payment/success`;
      const resp = await createCdnTopupPayment(selected.gb, returnUrl, appliedPromo?.code);

      if (resp.confirmationUrl) {
        // Редирект на ЮKassa (или stub-страницу) — как при оплате подписки
        window.location.href = resp.confirmationUrl;
        return;
      }

      // Stub-режим без редиректа: опрашиваем статус платежа
      const paymentId = resp.paymentId;
      let attempts = 0;
      pollRef.current = setInterval(async () => {
        attempts++;
        try {
          const payment = await getPaymentStatus(paymentId);
          if (payment.status === 'succeeded') {
            stopPolling();
            setPaying(false);
            onClose();
            toast.success(CDN_TOPUP_SUCCESS, { duration: 6000 });
            onPurchased?.();
          } else if (payment.status === 'cancelled' || attempts > POLL_MAX_ATTEMPTS) {
            stopPolling();
            setPaying(false);
            setError('Оплата не прошла');
          }
        } catch {
          if (attempts > POLL_MAX_ATTEMPTS) {
            stopPolling();
            setPaying(false);
            setError('Не удалось проверить статус оплаты');
          }
        }
      }, POLL_INTERVAL_MS);
    } catch (err) {
      setPaying(false);
      setError(getApiErrorMessage(err, 'Ошибка при создании платежа. Попробуйте позже'));
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={CDN_TOPUP_MODAL_TITLE}>
      <h3 className={styles.modalTitle}>{CDN_TOPUP_MODAL_TITLE}</h3>
      <p className={styles.hint}>{CDN_TOPUP_MODAL_HINT}</p>

      {packagesLoading && (
        <div className={styles.loading}>
          <Spinner />
        </div>
      )}

      {!packagesLoading && packages.length === 0 && (
        <div className={styles.error}>{CDN_TOPUP_PACKAGES_UNAVAILABLE}</div>
      )}

      {packages.length > 0 && (
        <fieldset className={styles.packages} disabled={paying}>
          <legend className={styles.legend}>{CDN_TOPUP_PACKAGES_LABEL}</legend>
          <div className={styles.packagesGrid}>
            {packages.map((pkg) => (
              <label key={pkg.gb} className={styles.pkg}>
                <input
                  type="radio"
                  name="cdn-package"
                  className={styles.pkgInput}
                  value={pkg.gb}
                  aria-label={cdnPackageLabel(pkg)}
                  checked={selected?.gb === pkg.gb}
                  onChange={() => setSelectedGb(pkg.gb)}
                />
                <span className={styles.pkgBody}>
                  <span className={styles.pkgGb}>{pkg.gb} ГБ</span>
                  <span className={styles.pkgPrice}>{formatPrice(pkg.priceKopecks)}</span>
                  <span className={styles.pkgPerGb}>{cdnPackagePerGb(pkg)}</span>
                </span>
              </label>
            ))}
          </div>
        </fieldset>
      )}

      {selected && (
        <div className={styles.totalRow}>
          <span className={styles.totalLabel}>Итого</span>
          <span>
            {hasPercent && <span className={styles.oldPrice}>{formatPrice(basePriceKopecks)}</span>}
            <span className={styles.totalPrice}>{formatPrice(finalPriceKopecks)}</span>
          </span>
        </div>
      )}

      <div className={styles.promoSection}>
        {appliedPromo ? (
          <div className={styles.promoApplied}>
            <span className={styles.promoCodeLabel}>{appliedPromo.code}</span>
            {hasPercent && (
              <span className={styles.promoBonus}>Скидка {appliedPromo.discountPercent}%</span>
            )}
            <button type="button" className={styles.promoRemove} onClick={handleRemovePromo}>
              &#10005; убрать
            </button>
          </div>
        ) : (
          <>
            <div className={styles.promoInputRow}>
              <input
                className={styles.promoInput}
                type="text"
                placeholder="Промокод"
                value={promoCode}
                onChange={(e) => setPromoCode(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleApplyPromo();
                  }
                }}
                disabled={promoLoading || paying}
                aria-label="Промокод"
              />
              <Button
                variant="secondary"
                onClick={handleApplyPromo}
                loading={promoLoading}
                disabled={!promoCode.trim() || promoLoading || paying}
              >
                Применить
              </Button>
            </div>
            {promoError && <div className={styles.promoError}>{promoError}</div>}
          </>
        )}
      </div>

      {error && <div className={styles.error}>{error}</div>}

      <Button fullWidth size="lg" onClick={handlePay} loading={paying} disabled={!selected}>
        Перейти к оплате {selected ? formatPrice(finalPriceKopecks) : ''}
      </Button>
    </Modal>
  );
}
